import {
  AC_TYPES,
  AUTOMATION_ITEMS,
  BALCONY_DOOR_TYPES,
  BALCONY_RAILING,
  BASIN_TYPES,
  CARCASS_MATERIAL,
  CEILING_DESIGN_OPTIONS,
  CIVIL_ITEMS,
  COUNTER_MATERIAL,
  COVE_LIGHTING_OPTIONS,
  CURTAIN_OPTIONS,
  FALSE_CEILING_TYPES,
  FITTING_BRANDS,
  FLOOR_COVERING,
  FLOOR_COVERINGS_BATH,
  HARDWARE_LEVELS,
  HEAVY_APPLIANCES,
  KITCHEN_CONSTRUCTION,
  KITCHEN_FITTINGS,
  LIGHT_CATEGORIES,
  PAINT_OPTIONS,
  PLUMBING_BRANDS,
  PROJECT_DELIVERABLES,
  PROPERTY_TYPES,
  RO_LOCATION,
  SERVICES_REQUIRED,
  SHOWER_TYPES,
  SHUTTER_FINISH,
  SWITCH_BRANDS,
  UNIT_TYPES,
  WALL_COVERINGS_BATH,
  WALL_PANEL_MATERIALS,
  WARDROBE_HEIGHTS,
  WARDROBE_INTERNALS,
  WARDROBE_TYPES,
  WASHROOM_CIVIL,
  WINDOW_COVERING,
  WINDOW_TYPES,
  WIRE_TYPES,
  WIRING_BRANDS,
  YES_NO,
} from './scopeSheetOptions';

/** Field helpers */
const text = (id, label, extra = {}) => ({
  id,
  label,
  type: 'text',
  ...extra,
});
const number = (id, label, extra = {}) => ({
  id,
  label,
  type: 'number',
  ...extra,
});
const notes = (id = 'notes', label = 'Notes', extra = {}) => ({
  id,
  label,
  type: 'richNotes',
  ...extra,
});
const richNotes = (id = 'notes', label = 'Notes', extra = {}) =>
  notes(id, label, extra);
const yesNo = (id, label, extra = {}) => ({
  id,
  label,
  type: 'yesNo',
  options: YES_NO,
  ...extra,
});
const single = (id, label, options, extra = {}) => ({
  id,
  label,
  type: 'singleSelect',
  options,
  ...extra,
});
const multi = (id, label, options, extra = {}) => ({
  id,
  label,
  type: 'checkboxGroup',
  options,
  ...extra,
});

const whenYes = (field) => ({ visibleWhen: { field, equals: 'Yes' } });

const dims = () => [
  text('length', 'Length'),
  text('width', 'Width'),
  text('ceilingHeight', 'Ceiling Height'),
];

const windowDims = () => [
  text('windowCount', 'Window Count'),
  text('sillHeight', 'Sill Height'),
  text('lintelHeight', 'Lintel Height'),
];

const balconyAccessFields = () => [
  yesNo('balconyAccess', 'Balcony Access'),
  multi(
    'balconyDoor',
    'Balcony Door',
    BALCONY_DOOR_TYPES,
    whenYes('balconyAccess')
  ),
  text('balconyDoorWidth', 'Door Size — Width', whenYes('balconyAccess')),
  text('balconyDoorHeight', 'Door Size — Height', whenYes('balconyAccess')),
  multi(
    'balconyRailing',
    'Balcony Railing',
    BALCONY_RAILING,
    whenYes('balconyAccess')
  ),
  text(
    'balconyRailingSize',
    'Railing Size (r.ft / sq.ft)',
    whenYes('balconyAccess')
  ),
];

const civilSubsection = (id = 'civil') => ({
  id,
  title: 'civil',
  fields: [multi('items', 'Civil works', CIVIL_ITEMS), notes()],
});

const falseCeilingSubsection = () => ({
  id: 'falseCeiling',
  title: 'false ceiling',
  fields: [
    {
      id: 'required',
      label: 'False Ceiling Required',
      type: 'checkbox',
    },
    multi('types', 'False Ceiling Type', FALSE_CEILING_TYPES, {
      visibleWhen: { field: 'required', equals: true },
    }),
    multi('coveLighting', 'Cove Lighting Options', COVE_LIGHTING_OPTIONS, {
      visibleWhen: { field: 'required', equals: true },
    }),
    multi('ceilingDesign', 'Ceiling Design Options', CEILING_DESIGN_OPTIONS, {
      visibleWhen: { field: 'required', equals: true },
    }),
    notes(),
  ],
});

const floorCoveringSubsection = () => ({
  id: 'floorCovering',
  title: 'floor covering',
  fields: [multi('materials', 'Floor covering', FLOOR_COVERING), notes()],
});

const softFurnishingSubsection = (extra = []) => ({
  id: 'softFurnishing',
  title: 'soft furnishing',
  fields: [
    single('curtains', 'Curtains', CURTAIN_OPTIONS),
    multi('windowCovering', 'Window Covering', WINDOW_COVERING),
    ...extra,
    notes(),
  ],
});

const electricalCoreFields = () => [
  multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
  multi('wireType', 'Wire Type / Safety', WIRE_TYPES),
  multi('switches', 'Switches', SWITCH_BRANDS),
  multi('lightCategories', 'Light Categories', LIGHT_CATEGORIES),
  number('wallLightsQty', 'Wall Lights (Qty)'),
  number('fansQty', 'Fans (Qty)'),
  number('acWiringQty', 'AC Wiring (Qty)'),
  yesNo('speakersRequired', 'Speakers Required'),
  yesNo('automationRequired', 'Automation Required'),
  multi(
    'automationItems',
    'Automation Items',
    AUTOMATION_ITEMS,
    whenYes('automationRequired')
  ),
  multi('acTypes', 'Air Condition Types', AC_TYPES),
  text('approxWiringLength', 'Approx. Wiring Length'),
];

/** Foyer: stop at light categories; rest under Additional Electrical. */
const foyerElectricalCoreFields = () => [
  multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
  multi('wireType', 'Wire Type / Safety', WIRE_TYPES),
  multi('switches', 'Switches', SWITCH_BRANDS),
  multi('lightCategories', 'Light Categories', LIGHT_CATEGORIES),
];

/** Living / Drawing: stop at light categories (incl. Cove Lights); rest under Other Electricals. */
const livingElectricalCoreFields = () => [
  multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
  multi('wireType', 'Wire Type / Safety', WIRE_TYPES),
  multi('switches', 'Switches', SWITCH_BRANDS),
  multi('lightCategories', 'Light Categories', LIGHT_CATEGORIES),
];

const livingOtherElectricalFields = (extra = []) => [
  {
    id: 'wallLights',
    label: 'Wall Lights',
    type: 'checkbox',
  },
  number('wallLightsQty', 'Wall Lights (Qty)', {
    visibleWhen: { field: 'wallLights', equals: true },
  }),
  {
    id: 'fans',
    label: 'Fans',
    type: 'checkbox',
  },
  number('fansQty', 'Fans (Qty)', {
    visibleWhen: { field: 'fans', equals: true },
  }),
  {
    id: 'acWiring',
    label: 'AC Wiring',
    type: 'checkbox',
  },
  number('acWiringQty', 'AC Wiring (Qty)', {
    visibleWhen: { field: 'acWiring', equals: true },
  }),
  {
    id: 'chandelier',
    label: 'Chandelier',
    type: 'checkbox',
  },
  {
    id: 'speakersRequired',
    label: 'Speakers Required',
    type: 'checkbox',
  },
  {
    id: 'automationRequired',
    label: 'Automation Required',
    type: 'checkbox',
  },
  multi('automationItems', 'Automation Items', AUTOMATION_ITEMS, {
    visibleWhen: { field: 'automationRequired', equals: true },
  }),
  ...extra,
  multi('acTypes', 'Air Condition Types', AC_TYPES),
  text('approxWiringLength', 'Approx. Wiring Length'),
];

const foyerAdditionalElectricalFields = () => [
  {
    id: 'wallLights',
    label: 'Wall Lights',
    type: 'checkbox',
  },
  number('wallLightsQty', 'Wall Lights (Qty)', {
    visibleWhen: { field: 'wallLights', equals: true },
  }),
  {
    id: 'consoleLedStrip',
    label: 'Console LED Strip',
    type: 'checkbox',
  },
  {
    id: 'acWiring',
    label: 'AC Wiring',
    type: 'checkbox',
  },
  {
    id: 'speakersRequired',
    label: 'Speakers Required',
    type: 'checkbox',
  },
  {
    id: 'automationRequired',
    label: 'Automation Required',
    type: 'checkbox',
  },
  multi('automationItems', 'Automation Items', AUTOMATION_ITEMS, {
    visibleWhen: { field: 'automationRequired', equals: true },
  }),
  multi('entranceBell', 'Entrance Bell', [
    'Normal Bell',
    'Smart Bell (WiFi)',
    'Bell With Video Screen / Digital Door Camera',
  ]),
  multi('acTypes', 'Air Condition Types', AC_TYPES),
  text('approxWiringLength', 'Approx. Wiring Length'),
];

const paintFields = () => [
  multi('wallPaint', 'Wall Paint Options', PAINT_OPTIONS),
  multi('ceilingPaint', 'Ceiling Paint Options', PAINT_OPTIONS),
  notes(),
];

const hardwareField = () => multi('hardwareLevel', 'Hardware Level', HARDWARE_LEVELS);

const wallPanellingFields = ({ includeSizes = false } = {}) => [
  yesNo('wallPanellingRequired', 'Wall Panelling Required'),
  ...(includeSizes
    ? [
        single(
          'numberOfWalls',
          'Number of Walls',
          ['1', '2', '3'],
          whenYes('wallPanellingRequired')
        ),
      ]
    : []),
  multi(
    'wallMaterials',
    'Materials',
    WALL_PANEL_MATERIALS,
    whenYes('wallPanellingRequired')
  ),
  ...(includeSizes
    ? [
        text('wallSize1', 'Wall Size 1', {
          visibleWhen: [
            { field: 'wallPanellingRequired', equals: 'Yes' },
            { field: 'numberOfWalls', in: ['1', '2', '3'] },
          ],
        }),
        text('wallSize2', 'Wall Size 2', {
          visibleWhen: [
            { field: 'wallPanellingRequired', equals: 'Yes' },
            { field: 'numberOfWalls', in: ['2', '3'] },
          ],
        }),
        text('wallSize3', 'Wall Size 3', {
          visibleWhen: [
            { field: 'wallPanellingRequired', equals: 'Yes' },
            { field: 'numberOfWalls', in: ['3'] },
          ],
        }),
      ]
    : []),
];

const roomBasicSubsection = (includeBalcony = true, { includeWindowType = true } = {}) => ({
  id: 'basicInfo',
  title: 'basic info',
  fields: [
    ...dims(),
    ...windowDims(),
    ...(includeWindowType
      ? [multi('windowType', 'Window Type', WINDOW_TYPES)]
      : []),
    ...(includeBalcony ? balconyAccessFields() : []),
    notes(),
  ],
});

/** Kitchen: window info as its own block (balcony follows in next subsection). */
const kitchenWindowInfoSubsection = () => ({
  id: 'windowInfo',
  title: 'window information',
  fields: [...windowDims(), notes()],
});

const kitchenBalconyAccessSubsection = () => ({
  id: 'balconyAccess',
  title: 'balcony access',
  fields: balconyAccessFields(),
});

const kitchenBasicSubsection = () => ({
  id: 'basicInfo',
  title: 'basic info',
  fields: [...dims(), notes()],
});

const livingLikeSections = (letter, title, extras = {}) => {
  const subsections = [
    roomBasicSubsection(true, {
      includeWindowType: extras.includeWindowType !== false,
    }),
    civilSubsection(),
    falseCeilingSubsection(),
    floorCoveringSubsection(),
    softFurnishingSubsection(
      extras.softExtra || [
        text('rug', 'Rug'),
        text('upholstery', 'Upholstery Requirements'),
      ]
    ),
    {
      id: 'carpentry',
      title: 'carpentry',
      fields: [
        ...wallPanellingFields({
          includeSizes: Boolean(extras.includeWallPanellingSizes),
        }),
        ...(extras.carpentryExtra || []),
        hardwareField(),
        notes(),
      ],
    },
    ...(extras.electricalSubsections || [
      {
        id: 'electrical',
        title: 'electrical',
        fields: [
          ...electricalCoreFields(),
          ...(extras.electricalExtra || []),
          notes(),
        ],
      },
    ]),
    {
      id: 'paint',
      title: 'paint',
      fields: paintFields(),
    },
  ];
  return {
    id: letter,
    letter,
    title,
    subsections,
  };
};

const washroomSubsections = (label = 'washroom') => [
  {
    id: 'washroomBasic',
    title: `${label} — basic`,
    fields: [...dims(), ...windowDims(), notes()],
  },
  {
    id: 'washroomCivil',
    title: `${label} — civil`,
    fields: [multi('items', 'Civil', WASHROOM_CIVIL), notes()],
  },
  {
    id: 'washroomWalls',
    title: `${label} — wall coverings`,
    fields: [
      multi('materials', 'Wall coverings', WALL_COVERINGS_BATH),
      text('groutColor', 'Grout Color'),
      notes(),
    ],
  },
  {
    id: 'washroomFloor',
    title: `${label} — floor coverings`,
    fields: [
      multi('materials', 'Floor coverings', FLOOR_COVERINGS_BATH),
      text('groutColor', 'Grout Color'),
      notes(),
    ],
  },
  {
    id: 'washroomBasin',
    title: `${label} — basin`,
    fields: [multi('basinType', 'Basin Type', BASIN_TYPES), notes()],
  },
  {
    id: 'washroomShower',
    title: `${label} — shower`,
    fields: [multi('showerType', 'Shower Type', SHOWER_TYPES), notes()],
  },
  {
    id: 'washroomFittings',
    title: `${label} — fittings & plumbing`,
    fields: [
      multi('fittingBrands', 'Fitting Brands', FITTING_BRANDS),
      text('fittingOther', 'Other fitting brand'),
      multi('plumbingMaterial', 'Plumbing Material', PLUMBING_BRANDS),
      notes(),
    ],
  },
  {
    id: 'washroomElectrical',
    title: `${label} — electrical`,
    fields: [
      multi('items', 'Electrical', [
        'Mirror Light',
        'Ceiling Light',
        'Exhaust Fan',
        'Geyser',
      ]),
      multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
      multi('wireType', 'Wire Type', WIRE_TYPES),
      multi('switchType', 'Switch Type', SWITCH_BRANDS),
      multi('ceilingPaint', 'Ceiling Paint', PAINT_OPTIONS),
      notes(),
    ],
  },
];

const bedroomWardrobeSubsection = () => ({
  id: 'wardrobe',
  title: 'wardrobe',
  fields: [
    multi('wardrobeType', 'Wardrobe Type', WARDROBE_TYPES),
    multi('wardrobeHeight', 'Wardrobe Height', WARDROBE_HEIGHTS),
    text('wardrobeWidth', 'Width'),
    text('wardrobeDepth', 'Depth'),
    text('loftHeight', 'Loft Height'),
    multi('internalLayout', 'Internal Layout', WARDROBE_INTERNALS),
    number('shelvesQty', 'Shelves (Qty)'),
    number('drawersQty', 'Drawers (Qty)'),
    hardwareField(),
    notes(),
  ],
});

export const MAX_BEDROOMS = 5;

export const buildBedroomSection = (index) => ({
  id: `bedroom-${index}`,
  letter: null,
  bedroomIndex: index,
  isBedroomSection: true,
  title: `Bedroom ${index} & Washroom ${index}`,
  subsections: [
    roomBasicSubsection(true, { includeWindowType: false }),
    civilSubsection(),
    falseCeilingSubsection(),
    floorCoveringSubsection(),
    softFurnishingSubsection([
      text('rug', 'Rug'),
      text('upholstery', 'Upholstery Requirements'),
    ]),
    {
      id: 'carpentry',
      title: 'carpentry',
      fields: [...wallPanellingFields({ includeSizes: true }), notes()],
    },
    bedroomWardrobeSubsection(),
    {
      id: 'electrical',
      title: 'electrical',
      fields: [...electricalCoreFields(), notes()],
    },
    {
      id: 'paint',
      title: 'paint',
      fields: paintFields(),
    },
    ...washroomSubsections(`washroom ${index}`),
  ],
});

export const ALL_BEDROOM_SECTIONS = Array.from({ length: MAX_BEDROOMS }, (_, i) =>
  buildBedroomSection(i + 1)
);

/** Parse 2BHK / 3BHK etc. Supports string or legacy checkbox array. */
export const getBedroomCountFromScope = (scopeSheet) => {
  const unitType = scopeSheet?.A?.basicInfo?.unitType;
  const raw = Array.isArray(unitType) ? unitType[0] : unitType;
  const match = String(raw || '').match(/(\d)\s*BHK/i);
  if (!match) return 0;
  const count = Number(match[1]);
  if (Number.isNaN(count) || count < 1) return 0;
  return Math.min(count, MAX_BEDROOMS);
};

const assignLetters = (sections) =>
  sections.map((section, index) => ({
    ...section,
    letter: String.fromCharCode(65 + index),
  }));

const filterSectionAForBedrooms = (sectionA, bedroomCount) => {
  if (!sectionA) return sectionA;
  return {
    ...sectionA,
    subsections: (sectionA.subsections || []).map((subsection) => {
      if (subsection.id !== 'ceilingHeight') return subsection;
      return {
        ...subsection,
        fields: (subsection.fields || []).filter((field) => {
          const match = String(field.id || '').match(/^bedroom(\d)CeilingHeight$/);
          if (!match) return true;
          if (bedroomCount < 1) return false;
          return Number(match[1]) <= bedroomCount;
        }),
      };
    }),
  };
};

const emptyBalconyInstance = () => ({
  label: '',
  basic: {
    length: '',
    width: '',
    ceilingHeight: '',
    doorType: [],
    doorSize: '',
    railingType: [],
    railingHeight: '',
    notes: '',
  },
  civil: { items: [], notes: '' },
  floor: { materials: [], tileSize: '', groutColor: '', notes: '' },
  walls: { materials: [], featureWallMaterial: '', notes: '' },
  ceiling: {
    finishes: [],
    waterproofCoat: '',
    lighting: '',
    fanPoint: '',
    notes: '',
  },
  carpentry: {
    items: [],
    seatingMaterial: '',
    storageMaterial: '',
    privacyMaterial: '',
    notes: '',
  },
  looseFurniture: {
    outdoorChairsQty: '',
    outdoorTableQty: '',
    outdoorSofa: '',
    swing: '',
    swingType: '',
    weatherproofCushions: '',
    notes: '',
  },
  electrical: { items: [], notes: '' },
  plumbing: {
    waterTap: '',
    tapType: '',
    drainPosition: '',
    rerouting: '',
    notes: '',
  },
  greenery: { items: [], material: '', notes: '' },
  safety: { items: [], notes: '' },
});

export const BALCONY_TEMPLATE_FIELDS = {
  basic: [
    text('length', 'Length'),
    text('width', 'Width'),
    text('ceilingHeight', 'Ceiling Height'),
    multi('doorType', 'Door Type', BALCONY_DOOR_TYPES),
    text('doorSize', 'Door/Window Size (W x H)'),
    multi('railingType', 'Railing Type', [
      'Glass',
      'MS',
      'SS',
      'Brick',
      'Other',
    ]),
    text('railingHeight', 'Railing Height'),
    notes(),
  ],
  civil: [
    multi('items', 'Civil', [
      'Waterproofing Required',
      'Slope Correction',
      'PCC / Leveling',
      'Tile Removal',
      'Wall Plaster Repair',
      'Ceiling Repair / Waterproofing',
      'Drain Cleaning / New Drain',
    ]),
    notes(),
  ],
  floor: [
    multi('materials', 'Floor Coverings', [
      'Anti-Skid Tile',
      'Stone',
      'Granite',
      'Wooden Tile',
      'Concrete Texture',
    ]),
    text('tileSize', 'Tile/Stone Size'),
    text('groutColor', 'Grout Color'),
    notes(),
  ],
  walls: [
    multi('materials', 'Wall Coverings', [
      'Exterior Paint',
      'Texture Paint',
      'Stone Cladding',
      'Tile Cladding',
      'Brick Cladding',
    ]),
    text('featureWallMaterial', 'Feature Wall Material'),
    notes(),
  ],
  ceiling: [
    multi('finishes', 'Ceiling', [
      'Exterior Paint',
      'Cement Sheet Ceiling',
      'Wooden Slats',
      'Metal / UPVC Ceiling',
    ]),
    yesNo('waterproofCoat', 'Waterproof Coat Required'),
    yesNo('lighting', 'Lighting'),
    yesNo('fanPoint', 'Fan Point'),
    notes(),
  ],
  carpentry: [
    multi('items', 'Built-In Elements', [
      'Seating Bench',
      'Storage Unit',
      'Outdoor Cabinet',
      'Planter Box (Built-In)',
      'Privacy Screen / Jaali',
    ]),
    text('seatingMaterial', 'Seating Material'),
    text('storageMaterial', 'Storage Material'),
    text('privacyMaterial', 'Privacy Screen Material'),
    notes(),
  ],
  looseFurniture: [
    number('outdoorChairsQty', 'Outdoor Chairs (Qty)'),
    number('outdoorTableQty', 'Outdoor Table (Qty)'),
    yesNo('outdoorSofa', 'Outdoor Sofa'),
    yesNo('swing', 'Swing / Jhoola'),
    single('swingType', 'Swing Type', ['Rope', 'Wood', 'Metal'], whenYes('swing')),
    yesNo('weatherproofCushions', 'Weatherproof Cushions'),
    notes(),
  ],
  electrical: [
    multi('items', 'Electrical', [
      'Wall Lamps',
      'Outdoor Spotlights',
      'Outdoor Strip Lights',
      'Profile Lights',
      'Fan Point',
      'Heater Point',
      'Speakers',
      'CCTV / Camera Point',
    ]),
    notes(),
  ],
  plumbing: [
    yesNo('waterTap', 'Water Tap Required'),
    single(
      'tapType',
      'Tap Type',
      ['Standard', 'Garden Tap', 'Nozzle Tap'],
      whenYes('waterTap')
    ),
    text('drainPosition', 'Drain Position'),
    yesNo('rerouting', 'Re-routing Required'),
    notes(),
  ],
  greenery: [
    multi('items', 'Greenery / Planters', [
      'Pots Required (Small / Medium / Large)',
      'Drip Irrigation',
      'Artificial Turf',
      'Vertical Garden',
    ]),
    text('material', 'Material'),
    notes(),
  ],
  safety: [
    multi('items', 'Safety', [
      'Child Safety Grill',
      'Bird Net',
      'Anti-Slip Coating',
      'Check Railing Height Compliance',
    ]),
    notes(),
  ],
};

export { emptyBalconyInstance };

export const SCOPE_SHEET_SECTIONS = [
  {
    id: 'A',
    letter: 'A',
    title: 'Project Information',
    subsections: [
      {
        id: 'basicInfo',
        title: 'basic info',
        fields: [
          text('projectAddress', 'Project Address'),
          text('clientNames', 'Client Name(s)'),
          multi('propertyType', 'Property Type', PROPERTY_TYPES),
          single(
            'unitType',
            'Number of bedrooms (Unit Type)',
            UNIT_TYPES
          ),
          text('totalCarpetArea', 'Total Carpet Area'),
          notes(),
        ],
      },
      {
        id: 'ceilingHeight',
        title: 'ceiling & height information',
        fields: [
          text('generalCeilingHeight', 'General Ceiling Height'),
          text('floorToFloorHeight', 'Floor-to-Floor Height'),
          text('foyerCeilingHeight', 'Foyer Ceiling Height'),
          text('livingCeilingHeight', 'Living Ceiling Height'),
          text('diningCeilingHeight', 'Dining Ceiling Height'),
          text('kitchenCeilingHeight', 'Kitchen Ceiling Height'),
          text('bedroom1CeilingHeight', 'Bedroom 1 Ceiling Height'),
          text('bedroom2CeilingHeight', 'Bedroom 2 Ceiling Height'),
          text('bedroom3CeilingHeight', 'Bedroom 3 Ceiling Height'),
          text('bedroom4CeilingHeight', 'Bedroom 4 Ceiling Height'),
          text('bedroom5CeilingHeight', 'Bedroom 5 Ceiling Height'),
          text('storeRoomCeilingHeight', 'Store Room Ceiling Height'),
          text('helpRoomCeilingHeight', 'Help Room Ceiling Height'),
          notes(),
        ],
      },
      {
        id: 'windowInfo',
        title: 'window information',
        fields: [
          text('windowCount', 'Window Count (per room)'),
          text('sillHeight', 'Sill Height'),
          text('lintelHeight', 'Lintel Height'),
          multi('windowType', 'Window Type', WINDOW_TYPES),
          notes(),
        ],
      },
    ],
  },
  {
    id: 'B',
    letter: 'B',
    title: 'Global Project Scope',
    subsections: [
      {
        id: 'servicesRequired',
        title: 'services required',
        fields: [
          multi('selected', 'Services required', SERVICES_REQUIRED),
          notes(),
        ],
      },
    ],
  },
  {
    id: 'C',
    letter: 'C',
    title: 'Project Deliverables',
    subsections: [
      {
        id: 'deliverables',
        title: 'deliverables',
        fields: [
          multi('selected', 'Project deliverables', PROJECT_DELIVERABLES),
          notes(),
        ],
      },
    ],
  },
  {
    id: 'D',
    letter: 'D',
    title: 'Main Entrance',
    subsections: [
      {
        id: 'basicInfo',
        title: 'basic info',
        fields: [...dims(), notes()],
      },
      {
        id: 'doorSystem',
        title: 'door system & size',
        fields: [
          multi('doorTypes', 'Door system', [
            'Safety Door',
            'Main Wooden Door',
            'Two-Door System',
          ]),
          text('doorWidth', 'Width'),
          text('doorHeight', 'Height'),
          notes(),
        ],
      },
      {
        id: 'carpentry',
        title: 'carpentry',
        fields: [
          multi('items', 'Carpentry', [
            'Shoe Rack',
            'Mirror',
            'Seating Near Shoerack',
          ]),
          notes(),
        ],
      },
      {
        id: 'electrical',
        title: 'electrical',
        fields: [
          multi('items', 'Electrical', [
            'Wall Light',
            'Ceiling Lights',
            'Bell Wiring',
            'Staircase Light',
          ]),
          notes(),
        ],
      },
      {
        id: 'decor',
        title: 'decor',
        fields: [
          multi('items', 'Decor', [
            'Planters',
            'Wall decor',
            'Rugs/Mats',
            'Entrance Flooring',
          ]),
          notes(),
        ],
      },
    ],
  },
  livingLikeSections('E', 'Foyer Area', {
    softExtra: [],
    carpentryExtra: [
      multi('consoleStorage', 'Console / Storage', [
        'Console Table',
        'Shoe Storage',
        'Coat Hanger',
        'Mirror Unit',
      ]),
      text('carpentryMaterial', 'Carpentry Material (Ply / HDHMR / MDF)'),
    ],
    electricalSubsections: [
      {
        id: 'electrical',
        title: 'electrical',
        fields: [...foyerElectricalCoreFields(), notes()],
      },
      {
        id: 'additionalElectrical',
        title: 'additional electrical',
        fields: foyerAdditionalElectricalFields(),
      },
    ],
  }),
  livingLikeSections('F', 'Living / Drawing Room', {
    includeWindowType: false,
    includeWallPanellingSizes: true,
    carpentryExtra: [
      multi('furnitureUnits', 'Furniture Units', [
        'TV Unit',
        'Crockery Unit',
        'Bar Unit',
        'Console',
        'Window Seating',
      ]),
      text('sofaSeater', 'Sofa (seater)'),
      number('armchairs', 'Armchairs'),
      number('loungeChairs', 'Lounge Chairs'),
      yesNo('coffeeTable', 'Coffee Table'),
      yesNo('sideTables', 'Side Tables'),
      yesNo('rug', 'Rug'),
    ],
    electricalSubsections: [
      {
        id: 'electrical',
        title: 'electrical',
        fields: [...livingElectricalCoreFields(), notes()],
      },
      {
        id: 'otherElectricals',
        title: 'other electricals',
        fields: livingOtherElectricalFields(),
      },
    ],
  }),
  livingLikeSections('G', 'Dining Area', {
    includeWallPanellingSizes: true,
    carpentryExtra: [
      multi('diningStorage', 'Dining Storage', [
        'Crockery Unit',
        'Console Table',
        'Buffet / Sideboard',
      ]),
      number('diningTablePersons', 'Dining Table for (persons)'),
      number('chairs', 'Chairs'),
      yesNo('benchSeating', 'Bench Seating'),
    ],
    electricalExtra: [yesNo('chandelier', 'Chandelier')],
  }),
  {
    id: 'H',
    letter: 'H',
    title: 'Kitchen',
    subsections: [
      kitchenBasicSubsection(),
      kitchenWindowInfoSubsection(),
      kitchenBalconyAccessSubsection(),
      civilSubsection(),
      falseCeilingSubsection(),
      floorCoveringSubsection(),
      {
        id: 'constructionType',
        title: 'construction type',
        fields: [
          multi('types', 'Construction type', KITCHEN_CONSTRUCTION),
          notes(),
        ],
      },
      {
        id: 'carpentry',
        title: 'carpentry',
        fields: [
          multi('carcassMaterial', 'Carcass Material', CARCASS_MATERIAL),
          multi('shutterFinish', 'Shutter Finish', SHUTTER_FINISH),
          hardwareField(),
          multi('fittings', 'Fittings', KITCHEN_FITTINGS),
          multi('counter', 'Counter', COUNTER_MATERIAL),
          text('backsplashMaterial', 'Backsplash Material'),
          notes(),
        ],
      },
      {
        id: 'electrical',
        title: 'electrical',
        fields: [
          multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
          multi('switches', 'Switches', SWITCH_BRANDS),
          multi('lightCategories', 'Light Categories', [
            'COB',
            'Panel Lights',
            'Profile Lights',
            'Cove Lights',
          ]),
          notes(),
        ],
      },
      {
        id: 'otherElectrical',
        title: 'other electrical',
        fields: [
          {
            id: 'fans',
            label: 'Fans',
            type: 'checkbox',
          },
          number('fansQty', 'Fans (Qty)', {
            visibleWhen: { field: 'fans', equals: true },
          }),
          {
            id: 'acWiring',
            label: 'AC Wiring',
            type: 'checkbox',
          },
          number('acWiringQty', 'AC Wiring (Qty)', {
            visibleWhen: { field: 'acWiring', equals: true },
          }),
          {
            id: 'speakersRequired',
            label: 'Speakers Required',
            type: 'checkbox',
          },
          multi('acTypes', 'Air Condition Types', AC_TYPES),
          text('approxWiringLength', 'Approx. Wiring Length'),
          multi('heavyAppliances', 'Heavy Appliances', HEAVY_APPLIANCES),
          multi('wireType', 'Wire Safety', WIRE_TYPES),
          multi('lights', 'Lights', [
            'Under-Cabinet LED',
            'Ceiling Lights',
            'Profile Lights',
          ]),
          notes(),
        ],
      },
      {
        id: 'paint',
        title: 'paint',
        fields: [
          multi('ceilingPaint', 'Ceiling Paint Options', PAINT_OPTIONS),
          notes(),
        ],
      },
      {
        id: 'plumbing',
        title: 'plumbing',
        fields: [
          yesNo('sinkRelocation', 'Sink Relocation'),
          yesNo('drainLineChange', 'Drain Line Change'),
          yesNo('dishwasherPlumbing', 'Dishwasher Plumbing'),
          yesNo('geyserRequired', 'Geyser Required'),
          single('roLocation', 'RO Location', RO_LOCATION),
          multi('plumbingBrand', 'Plumbing Material Brand', PLUMBING_BRANDS),
          notes(),
        ],
      },
    ],
  },
  // Bedroom sections are inserted dynamically via getVisibleScopeSections()
  {
    id: 'help-room',
    letter: 'L',
    title: 'Domestic Help Room',
    subsections: [
      {
        id: 'basicInfo',
        title: 'basic info',
        fields: [
          text('length', 'Length'),
          text('width', 'Width'),
          text('height', 'Height'),
          text('wardrobeDimensions', 'Wardrobe dimensions'),
          text('material', 'Material'),
          text('finish', 'Finish'),
          multi('hardwareLevel', 'Hardware level', HARDWARE_LEVELS),
          yesNo('bedNiche', 'Bed niche'),
          notes(),
        ],
      },
      {
        id: 'electrical',
        title: 'electrical',
        fields: [
          multi('wiringBrand', 'Wiring brand', WIRING_BRANDS),
          multi('wireType', 'Wire type', WIRE_TYPES),
          multi('switches', 'Switches', SWITCH_BRANDS),
          yesNo('fan', 'Fan'),
          text('lights', 'Lights'),
          yesNo('ventilation', 'Ventilation'),
          multi('paint', 'Paint', PAINT_OPTIONS),
          notes(),
        ],
      },
      {
        id: 'helpBathroom',
        title: 'help bathroom',
        fields: [
          text('tilesWallCovering', 'Tiles / wall covering'),
          text('floorCovering', 'Floor covering'),
          multi('basin', 'Basin', BASIN_TYPES),
          multi('fittings', 'Fittings', FITTING_BRANDS),
          multi('plumbingMaterial', 'Plumbing line material', PLUMBING_BRANDS),
          yesNo('exhaust', 'Exhaust'),
          multi('switches', 'Switches', SWITCH_BRANDS),
          yesNo('geyser', 'Geyser'),
          multi('paint', 'Paint', PAINT_OPTIONS),
          notes(),
        ],
      },
    ],
  },
  {
    id: 'store-room',
    letter: 'M',
    title: 'Store Room',
    subsections: [
      {
        id: 'basicInfo',
        title: 'basic info',
        fields: [
          text('length', 'Length'),
          text('width', 'Width'),
          text('ceilingHeight', 'Ceiling Height'),
          notes(),
        ],
      },
      {
        id: 'storeWardrobe',
        title: 'store wardrobe',
        fields: [
          text('width', 'Width (ft)'),
          text('height', 'Height (ft)'),
          multi('material', 'Material', [
            'Laminate',
            'Veneer',
            'PU',
            'Acrylic',
          ]),
          multi('carcass', 'Carcass', ['HDHMR', 'BWP', 'Ply']),
          multi('hardwareLevel', 'Hardware Level', HARDWARE_LEVELS),
          yesNo('lofts', 'Lofts'),
          yesNo('shelves', 'Shelves'),
          yesNo('lighting', 'Lighting'),
          yesNo('ventilation', 'Ventilation'),
          multi('paint', 'Paint (Walls + Ceiling)', PAINT_OPTIONS),
          multi('wiringBrand', 'Wiring Brand', WIRING_BRANDS),
          multi('switches', 'Switches', SWITCH_BRANDS),
          notes(),
        ],
      },
    ],
  },
  {
    id: 'balconies',
    letter: 'N',
    title: 'Balconies',
    type: 'balconyList',
    subsections: [],
  },
  {
    id: 'section-notes',
    letter: 'Q',
    title: 'Notes',
    subsections: [
      {
        id: 'notes',
        title: 'notes',
        fields: [richNotes('notes', 'Notes', { minHeight: 180 })],
      },
    ],
  },
];

const CORE_BEFORE_BEDROOMS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const CORE_AFTER_BEDROOMS = [
  'help-room',
  'store-room',
  'balconies',
  'section-notes',
];

const findStaticSection = (id) =>
  SCOPE_SHEET_SECTIONS.find((section) => section.id === id);

/** Full catalog used for empty data / lookups (includes all bedroom templates). */
export const getAllScopeSectionsCatalog = () => [
  ...CORE_BEFORE_BEDROOMS.map(findStaticSection).filter(Boolean),
  ...ALL_BEDROOM_SECTIONS,
  ...CORE_AFTER_BEDROOMS.map(findStaticSection).filter(Boolean),
];

/**
 * Sections shown in nav/forms based on Section A unit type (2BHK–5BHK).
 * Bedroom sections appear only after a unit type is selected.
 */
export const getVisibleScopeSections = (scopeSheet) => {
  const bedroomCount = getBedroomCountFromScope(scopeSheet);
  const before = CORE_BEFORE_BEDROOMS.map(findStaticSection).filter(Boolean);
  const bedrooms = ALL_BEDROOM_SECTIONS.slice(0, bedroomCount);
  const after = CORE_AFTER_BEDROOMS.map(findStaticSection).filter(Boolean);

  const sectionA = filterSectionAForBedrooms(before[0], bedroomCount);
  const ordered = [
    sectionA,
    ...before.slice(1),
    ...bedrooms,
    ...after,
  ].filter(Boolean);

  return assignLetters(ordered);
};

export const getSectionById = (id, scopeSheet) => {
  if (scopeSheet) {
    const visible = getVisibleScopeSections(scopeSheet);
    const found = visible.find((section) => section.id === id);
    if (found) return found;
  }
  return getAllScopeSectionsCatalog().find((section) => section.id === id);
};

export const SCOPE_SECTION_IDS = getAllScopeSectionsCatalog().map((s) => s.id);
